import { useEffect, useMemo } from "react";

import { VerificationStatus } from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { useAuthState } from "@/hooks/useAuthState";
import { usePreAsyncSetupStore } from "@/store/preAsyncSetup";
import { AssistantMessageTypes } from "@/types";

/**
 * Opens the pre-async setup panel from the conversation's latest `pre_async_requirements` event,
 * and closes it once the patient sends a message after it. The intro tells them they can keep
 * asking general questions instead, so writing in the chat counts as setting the panel aside;
 * the agent offers setup again when it is needed.
 *
 * Keyed on a patient message rather than on the event being the last message, because the backend
 * saves other assistant events after a turn's cards.
 *
 * :param allMessages: The conversation's messages, oldest first, or undefined while they load, so a
 *   reload doesn't read an empty list as "no open event" and close a panel in progress. A group
 *   stream includes the other conversations' messages, so the event is matched to `conversationId`.
 * :param conversationId: The conversation on screen.
 */
export function usePreAsyncSetupSession(
  allMessages: ClientEventModel[] | undefined,
  conversationId: string,
): void {
  const { isSignedIn } = useAuthState();
  // `isPending`, not `isLoading`: the profile query is disabled until auth initializes, and a
  // disabled query isn't "loading" even though there is no profile yet.
  const { data: profile, isPending: isProfilePending } = useGetUserProfile();
  const start = usePreAsyncSetupStore((s) => s.start);
  const clear = usePreAsyncSetupStore((s) => s.clear);
  const activeConversationId = usePreAsyncSetupStore(
    (s) => s.active?.conversationId,
  );
  const activeEventId = usePreAsyncSetupStore((s) => s.active?.eventId);

  const openEventId = useMemo(() => {
    if (!allMessages) return undefined;

    const eventIndex = allMessages.findLastIndex(
      (message) =>
        message.type === AssistantMessageTypes.PRE_ASYNC_REQUIREMENTS &&
        message.conversationId === conversationId,
    );
    if (eventIndex === -1) return undefined;

    const hasPatientReplied = allMessages
      .slice(eventIndex + 1)
      .some((message) => message.role === "user");
    if (hasPatientReplied) return undefined;

    return allMessages[eventIndex].id;
  }, [allMessages, conversationId]);

  // Only blocks opening: a panel already open sees approval arrive mid-flow and finishes itself.
  const isSetupDone = Boolean(
    isSignedIn &&
      profile?.idVerification?.status === VerificationStatus.approved,
  );
  const isOpenHere = activeConversationId === conversationId;
  const isOpenForEvent = Boolean(openEventId) && activeEventId === openEventId;

  useEffect(() => {
    if (isProfilePending || !allMessages) return;

    if (!openEventId) {
      if (isOpenHere) clear();
      return;
    }
    if (isSetupDone && !isOpenForEvent) return;

    start(conversationId, openEventId);
  }, [
    isProfilePending,
    allMessages,
    openEventId,
    isOpenHere,
    isSetupDone,
    isOpenForEvent,
    conversationId,
    start,
    clear,
  ]);
}
